/**
 * ExtractionPanel — orchestration UI du job d'extraction.
 *
 *  - Picker du file_id (via SubmissionFilesList)
 *  - Bouton "Lancer extraction" → POST /v1/extractions
 *  - Polling 2.5s (paramétrable) tant que pending|running
 *  - Timeout UI à 5 min → bascule "interrompu" + bouton "Relancer"
 *  - Affichage état (pending / running / done / failed) avec progression
 *    visuelle simple (spinner + libellé d'étape)
 *  - Si done : ExtractedFieldsList monté avec extractionId courant
 *
 * D27 (acceptée) : pas d'endpoint cancel serveur — le timeout est UI-only.
 * Le job MCP continue côté backend ; on prévient l'utilisateur avec un
 * libellé explicite "interrompu côté affichage, le job continue en arrière-
 * plan, le résultat sera visible au refresh".
 *
 * Décision PO 9.B : pas d'auto-sélection silencieuse si plusieurs fichiers.
 * Si un seul fichier ready → SubmissionFilesList le pré-sélectionne. Si 0
 * ou plusieurs → on bloque le bouton Lancer tant qu'aucun choix explicite.
 */

import { Prompts } from "@kollectivia/shared";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ExtractionReviewPanel } from "../../../components/extraction/ExtractionReviewPanel";
import {
  PdfUploadDropzone,
  type DropzoneState,
} from "../../../components/projects/PdfUploadDropzone";
import { ApiException } from "../../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../../lib/apiErrorMessage";
import { extractionErrorCodeKey } from "../../../lib/extractionErrorCode";
import { orchestrationKeys } from "../../../lib/queries/extractionOrchestrations";
import {
  useExtraction,
  useExtractionsByParent,
  useStartExtraction,
  type ExtractionRow,
  type ExtractionStatus,
} from "../../../lib/queries/extractions";
import { submissionsKeys, type SubmissionAttachedFile } from "../../../lib/queries/submissions";
import { toast } from "../../../lib/toast";

import { AnomaliesList } from "./AnomaliesList";
import { ExtractionOrchestrationPanel } from "./ExtractionOrchestrationPanel";
import {
  ALLOWED_TIMEOUT_VALUES_MS,
  type AllowedTimeoutMs,
  getExtractionTimeoutMs,
  setExtractionTimeoutMs,
} from "./extractionTimeout";
import { SubmissionFilesList } from "./SubmissionFilesList";

const POLL_INTERVAL_MS = 2500;

export type ExtractionPanelProps = {
  submissionId: string;
  files: SubmissionAttachedFile[];
  /**
   * Dernière extraction connue depuis le détail enrichi (sert d'amorce :
   * si elle est encore pending/running, on relance le polling dessus).
   */
  initialExtractionId?: string | null;
};

export function ExtractionPanel(props: ExtractionPanelProps) {
  const { submissionId, files, initialExtractionId } = props;
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const qc = useQueryClient();

  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
  const [activeExtractionId, setActiveExtractionId] = useState<string | null>(
    initialExtractionId ?? null,
  );
  const [timedOut, setTimedOut] = useState(false);
  const startedAtRef = useRef<number | null>(null);

  // 9.M.3 — timeout configurable via localStorage (paliers 60/120/300/600s).
  // Lu une fois au mount + après chaque changement utilisateur. Le ref interne
  // (`timeoutMsRef`) garde la valeur effective utilisée par le watcher pour
  // viter qu'un changement en cours de run ne déclenche `timed_out`
  // immédiatement (la valeur d'origine reste valide jusqu'au prochain
  // onLaunch/onRelaunch).
  const [timeoutMs, setTimeoutMsState] = useState<AllowedTimeoutMs>(() => getExtractionTimeoutMs());
  const timeoutMsRef = useRef<AllowedTimeoutMs>(timeoutMs);

  // Liste des extractions de la submission (timeline + permet de retrouver
  // la dernière non-terminale au mount).
  const list = useExtractionsByParent("submission", submissionId);

  // Au mount, si une extraction est encore pending|running, l'adopter.
  //
  // 9.AA.1b — remarque P2 Codex, fondée. Ce panneau est celui de
  // l'IDENTIFICATION. Depuis que l'orchestration métier crée jusqu'à 6
  // `app.extractions` sur la MÊME soumission, ce `find` sans filtre adoptait une
  // passe métier arbitraire : après un lancement puis un rafraîchissement, le bloc
  // d'identification affichait le statut — et la revue de champs — d'une passe
  // `dentaire` ou `amc`. Exactement la confusion entre les deux panneaux que
  // l'option C devait éviter.
  //
  // On filtre donc sur le contrat : `isBusinessExtractionPass` est dérivé de
  // `EXTRACTION_PASSES`, pas d'une liste en dur — une 8e passe ajoutée demain sera
  // exclue d'elle-même.
  useEffect(() => {
    if (activeExtractionId) return;
    const items = list.data?.items ?? [];
    const live = items.find(
      (e) =>
        (e.status === "pending" || e.status === "running") &&
        !Prompts.isBusinessExtractionPass(e.pass_id ?? undefined),
    );
    if (live) {
      setActiveExtractionId(live.id);
      startedAtRef.current = Date.now();
    }
  }, [list.data, activeExtractionId]);

  const start = useStartExtraction();
  const job = useExtraction(activeExtractionId ?? undefined, {
    pollIntervalMs: POLL_INTERVAL_MS,
    enabled: !!activeExtractionId && !timedOut,
  });

  // Même garde pour l'amorce reçue du parent. `initialExtractionId` vient de
  // `submission.last_extraction`, qui est la DERNIÈRE extraction quelle qu'elle
  // soit — donc très probablement une passe métier juste après un lancement. On ne
  // peut pas le filtrer à la source (le prop n'est qu'un identifiant), alors on le
  // relâche dès que le job chargé se révèle être une passe métier.
  useEffect(() => {
    if (!activeExtractionId) return;
    if (Prompts.isBusinessExtractionPass(job.data?.pass_id ?? undefined)) {
      setActiveExtractionId(null);
      startedAtRef.current = null;
      setTimedOut(false);
    }
  }, [job.data?.pass_id, activeExtractionId]);

  // Statut effectif (job.data ou null).
  const status: ExtractionStatus | null = job.data?.status ?? null;
  const isLive = status === "pending" || status === "running";

  // Timeout watcher — déclenche `timedOut=true` après `timeoutMsRef.current`
  // sans terminal (configurable 9.M.3, défaut 5 min).
  useEffect(() => {
    if (!activeExtractionId || !isLive) return;
    if (startedAtRef.current === null) {
      startedAtRef.current = Date.now();
    }
    const tick = () => {
      if (
        startedAtRef.current !== null &&
        Date.now() - startedAtRef.current > timeoutMsRef.current
      ) {
        setTimedOut(true);
      }
    };
    const id = window.setInterval(tick, 5000);
    return () => window.clearInterval(id);
  }, [activeExtractionId, isLive]);

  // Réinitialise startedAt quand le job devient terminal (le composant
  // peut redevenir "live" si on relance) — et arrête le timeout watch.
  useEffect(() => {
    if (status === "done" || status === "failed") {
      startedAtRef.current = null;
    }
  }, [status]);

  // 9.AA.1b — quand l'IDENTIFICATION réussit, le plan d'extraction des garanties
  // devient calculable. Il faut le dire au panneau du dessous.
  //
  // Remarque P2 Codex, fondée, et la plus gênante des trois : ce panneau-là affiche
  // « Lancez d'abord l'identification ci-dessus ». Or sa requête de plan a mis en
  // cache le 409 `IDENTIFICATION_REQUIRED`, avec `retry: false` et
  // `refetchOnWindowFocus: false`. Sans invalidation, le courtier suivait donc notre
  // propre consigne, l'identification aboutissait… et le bouton restait grisé
  // jusqu'à un rafraîchissement manuel de la page. Un cul-de-sac créé par notre
  // microcopy elle-même.
  //
  // On n'invalide que sur une identification RÉUSSIE : un échec ne change rien à la
  // calculabilité du plan, et une passe métier n'a pas à déclencher ça (elle ne
  // produit pas `sections_present`).
  useEffect(() => {
    if (status !== "done") return;
    if (job.data?.pass_id !== "identification") return;
    // Clés importées, jamais recopiées : une clé en dur ici se désynchroniserait en
    // silence du jour où `orchestrationKeys` change, et l'invalidation cesserait
    // d'agir sans qu'aucun test ne le voie.
    void qc.invalidateQueries({ queryKey: orchestrationKeys.plan(submissionId) });
    void qc.invalidateQueries({ queryKey: orchestrationKeys.list(submissionId) });
  }, [status, job.data?.pass_id, qc, submissionId]);

  const onLaunch = async () => {
    if (!selectedFileId) return;
    setTimedOut(false);
    startedAtRef.current = Date.now();
    // 9.M.3 — fige le timeout effectif pour ce run (re-lu depuis le state
    // qui est lui-même source de vérité pour la session courante).
    timeoutMsRef.current = timeoutMs;
    try {
      const row = await start.mutateAsync({
        source: { kind: "submission", id: submissionId },
        file_id: selectedFileId,
      });
      setActiveExtractionId(row.id);
    } catch (e: unknown) {
      const message =
        e instanceof ApiException
          ? getApiErrorMessage(e, lang) || t("errors.generic")
          : e instanceof Error
            ? e.message
            : t("errors.generic");
      toast.error(message);
      startedAtRef.current = null;
    }
  };

  const onRelaunch = () => {
    setActiveExtractionId(null);
    setTimedOut(false);
    startedAtRef.current = null;
    // Le `onLaunch` qui suit re-fige `timeoutMsRef` depuis le state courant
    // (potentiellement modifié par l'utilisateur via le radio dans le
    // bandeau timed_out).
    void onLaunch();
  };

  // 9.M.3 — setter exposé au radio du bandeau timed_out. Persiste le palier
  // global localStorage et met à jour le state local. Le ref est mis à jour
  // au prochain `onLaunch` (logique "prochain run").
  const onTimeoutChoice = (ms: AllowedTimeoutMs) => {
    setExtractionTimeoutMs(ms); // best-effort persist (refus silencieux si SSR/quota)
    setTimeoutMsState(ms);
  };

  const canLaunch = !!selectedFileId && !start.isPending && !isLive;
  const readyFilesCount = useMemo(() => files.filter((f) => f.status === "ready").length, [files]);

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <header className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-sm font-medium uppercase tracking-wide text-slate-500">
          {t("submissions.detail.extraction.title")}
        </h2>
      </header>

      {/* 9.M.2 — no-files state actionnable.
          Avant 9.M.2 on n'affichait qu'un texte invitant l'utilisateur à
          quitter la page (mauvaise UX : l'extraction est l'objet de la
          page, sans fichier on bloquait ici). On rend maintenant un
          PdfUploadDropzone inline avec parentIds={submission_id} :
            - bannière i18n explicative au-dessus,
            - dropzone branché sur le même submission (cf.
              `apps/api/src/routes/files.ts:initiate` qui accepte le tuple
              project|submission|company),
            - après onUploaded → invalidation submissionsKeys.detail(submissionId)
              pour que `useSubmission` refasse un GET /v1/submissions/:id
              et que `files` repasse de [] à [...] live (pas besoin de
              recharger la page).
          On ne touche pas à PdfUploadDropzone.tsx (contrainte scope). */}
      {files.length === 0 && (
        <div className="mb-4 space-y-2">
          <p className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
            {t("submissions.detail.extraction.no_files_invite")}
          </p>
          <p className="text-xs font-medium text-slate-700">
            {t("submissions.detail.extraction.upload_inline_title")}
          </p>
          <PdfUploadDropzone
            parentIds={{ submission_id: submissionId }}
            onStateChange={(s: DropzoneState) => {
              if (s.kind === "done") {
                toast.success(t("submissions.detail.extraction.upload_success_toast"));
                void qc.invalidateQueries({
                  queryKey: submissionsKeys.detail(submissionId),
                });
              }
            }}
          />
        </div>
      )}

      {/* Picker — toujours visible (jamais d'auto-sélection silencieuse) */}
      <div className="mb-4">
        <p className="mb-2 text-xs text-slate-500">
          {readyFilesCount === 0
            ? t("submissions.detail.extraction.no_ready_file")
            : readyFilesCount === 1
              ? t("submissions.detail.extraction.single_file_hint")
              : t("submissions.detail.extraction.pick_file_hint")}
        </p>
        <SubmissionFilesList
          files={files}
          selectedFileId={selectedFileId}
          onSelect={setSelectedFileId}
          disabled={isLive}
        />
      </div>

      {/* État du job courant */}
      {activeExtractionId && job.data && (
        <JobStatus
          row={job.data}
          timedOut={timedOut}
          startedAt={startedAtRef.current}
          lang={lang}
          timeoutMs={timeoutMs}
          onTimeoutChoice={onTimeoutChoice}
        />
      )}
      {job.error && (
        <p className="mb-3 rounded-md border border-red-200 bg-red-50 p-2 text-sm text-red-700">
          {job.error instanceof ApiException
            ? getApiErrorMessage(job.error, lang) || t("errors.generic")
            : t("errors.generic")}
        </p>
      )}

      {/* Actions */}
      <div className="mt-3 flex items-center gap-2">
        {!isLive && !timedOut && (
          <button
            type="button"
            onClick={onLaunch}
            disabled={!canLaunch}
            className="btn-primary text-sm"
          >
            {start.isPending
              ? t("submissions.detail.extraction.starting")
              : status === "done"
                ? t("submissions.detail.extraction.relaunch")
                : status === "failed"
                  ? t("submissions.detail.extraction.relaunch")
                  : t("submissions.detail.extraction.launch")}
          </button>
        )}
        {timedOut && (
          <button type="button" onClick={onRelaunch} className="btn-primary text-sm">
            {t("submissions.detail.extraction.relaunch")}
          </button>
        )}
      </div>

      {/* Champs extraits — visible dès qu'un job done existe. v3.3 : monte
          ExtractionReviewPanel neutre parent-agnostique au lieu de
          ExtractedFieldsList submission-only. */}
      {activeExtractionId && status === "done" && (
        <div className="mt-5 border-t border-slate-200 pt-4">
          <ExtractionReviewPanel
            parentKind="submission"
            parentId={submissionId}
            pinnedExtractionId={activeExtractionId}
          />
        </div>
      )}

      {/* 9.AA.1b — extraction des garanties (passes MÉTIER).
          Décision PO option C : composant dédié dans son propre fichier, inséré
          ici avec une intégration minimale. Ce panneau porte sa propre machine
          d'état (plan, confirmation, N passes, bilans) ; `ExtractionPanel` ne lui
          transmet que le fichier déjà choisi dans le sélecteur ci-dessus, pour
          qu'il n'existe qu'UNE vérité sur le document analysé.
          Le parcours d'identification au-dessus reste strictement inchangé. */}
      <ExtractionOrchestrationPanel submissionId={submissionId} fileId={selectedFileId} />

      {/* Anomalies — toujours visible (portées par submission, pas par extraction) */}
      <div className="mt-5 border-t border-slate-200 pt-4">
        <h3 className="mb-2 text-sm font-medium uppercase tracking-wide text-slate-500">
          {t("submissions.detail.anomalies.title")}
        </h3>
        <AnomaliesList submissionId={submissionId} />
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------------
// JobStatus — bandeau d'état (spinner + libellé + métadonnées)
// -----------------------------------------------------------------------------

function JobStatus({
  row,
  timedOut,
  startedAt,
  lang,
  timeoutMs,
  onTimeoutChoice,
}: {
  row: ExtractionRow;
  timedOut: boolean;
  startedAt: number | null;
  lang: "fr" | "en";
  timeoutMs: AllowedTimeoutMs;
  onTimeoutChoice: (ms: AllowedTimeoutMs) => void;
}) {
  const { t } = useTranslation();

  // Gap 1 (9.M.1) — elapsed fluide pendant pending|running.
  // Le polling refetch toutes les 2.5s ; sans tick local, le compteur saute
  // par marches (0s → 2s → 5s…). On force un re-render local 1s tant que
  // le statut est non-terminal et qu'on n'est pas en timed_out. Cleanup
  // au unmount + au passage terminal/timeout.
  const [, setNowTick] = useState(0);
  const isLive = row.status === "pending" || row.status === "running";
  useEffect(() => {
    if (!isLive || timedOut || startedAt === null) return;
    const id = window.setInterval(() => {
      setNowTick((n) => n + 1);
    }, 1000);
    return () => window.clearInterval(id);
  }, [isLive, timedOut, startedAt]);

  if (timedOut) {
    return (
      <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
        <p className="font-medium">{t("submissions.detail.extraction.timed_out_title")}</p>
        <p className="mt-1 text-xs text-amber-800">
          {t("submissions.detail.extraction.timed_out_body")}
        </p>
        {/* 9.M.3 — picker palier timeout. Affiché uniquement quand l'UI a
            déjà timed-out, pour proposer un palier plus large avant
            "Relancer". Persisté globalement (localStorage), pas par
            submission, pas par projet (décision PO Q-C). */}
        <fieldset className="mt-2">
          <legend className="text-xs font-medium text-amber-900">
            {t("submissions.detail.extraction.timeout_picker_legend")}
          </legend>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {ALLOWED_TIMEOUT_VALUES_MS.map((ms) => {
              const active = ms === timeoutMs;
              const label = t(`submissions.detail.extraction.timeout_options.${ms}`);
              return (
                <button
                  key={ms}
                  type="button"
                  aria-pressed={active}
                  onClick={() => onTimeoutChoice(ms)}
                  className={
                    active
                      ? "rounded-full border border-amber-400 bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-900"
                      : "rounded-full border border-amber-200 bg-white px-2.5 py-0.5 text-xs text-amber-900 hover:bg-amber-100"
                  }
                >
                  {label}
                </button>
              );
            })}
          </div>
        </fieldset>
      </div>
    );
  }

  const tone =
    row.status === "done"
      ? "border-green-200 bg-green-50 text-green-900"
      : row.status === "failed"
        ? "border-red-200 bg-red-50 text-red-900"
        : "border-brand-200 bg-brand-50 text-brand-900";

  // 9.M.2 — a11y polish : l'état failed est annoncé aux lecteurs d'écran.
  // running/pending/done restent muets côté ARIA (le spinner + libellé
  // visuel suffit). On scope role="alert" exclusivement à failed pour
  // ne pas spammer l'AT pendant un poll long.
  const isFailed = row.status === "failed";

  const elapsed =
    startedAt !== null ? Math.max(0, Math.floor((Date.now() - startedAt) / 1000)) : null;

  return (
    <div
      className={`mb-3 rounded-md border p-3 text-sm ${tone}`}
      role={isFailed ? "alert" : undefined}
    >
      <div className="flex items-center gap-2">
        {(row.status === "pending" || row.status === "running") && (
          <span
            aria-hidden="true"
            className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent"
          />
        )}
        <span className="font-medium">
          {t(`submissions.detail.extraction.status.${row.status}`)}
        </span>
        {elapsed !== null && (row.status === "pending" || row.status === "running") && (
          <span className="text-xs opacity-70">· {elapsed}s</span>
        )}
      </div>
      {row.pass_id && (
        <p className="mt-1 text-xs opacity-80">
          {t("submissions.detail.extraction.pass")}: {row.pass_id}
          {row.model_id ? ` · ${row.model_id}` : ""}
        </p>
      )}
      {/* 9.M.2 — sous-titre explicite quand failed (avant : rien sauf le
          libellé status déjà affiché plus haut). Indique à l'utilisateur
          que la relance est possible et que l'erreur détaillée suit. */}
      {isFailed && (
        <p className="mt-1 text-xs">{t("submissions.detail.extraction.failed_subtitle")}</p>
      )}
      {/* 9.W.2 — `error_code` est un code de type FERMÉ (cf.
          `ExtractionErrorCodeSchema`, apps/shared). On le localise via le
          catalogue i18n ; aucune regex, aucun `includes()`, et surtout AUCUN
          repli sur `error_message` — cette colonne pouvait porter le message du
          fournisseur ou un fragment du document client. Un code inconnu (ligne
          écrite par une version antérieure) tombe sur un libellé générique. */}
      {row.error_code && (
        <p className="mt-1 text-xs">{t(extractionErrorCodeKey(row.error_code))}</p>
      )}
    </div>
  );
}
